import { Meeting } from '../../types/meeting';
import { MeetingDetailsTab } from './tabs/MeetingDetailsTab';
import { TemplateList } from './TemplateList';
import { AttendeeList } from '@/components/attendees/AttendeeList';
import { AgendaBuilder } from '@/components/agenda/AgendaBuilder';
import { TaskList } from '@/components/tasks/TaskList';
import { FileSection } from './tabs/FileSection';

interface MeetingFormContentProps {
  activeTab: string;
  formData: Partial<Meeting>;
  setFormData: (data: Partial<Meeting>) => void;
  onShowRecurrence: () => void;
  setActiveTab: (tab: string) => void;
}

export function MeetingFormContent({
  activeTab,
  formData,
  setFormData,
  onShowRecurrence,
  setActiveTab
}: MeetingFormContentProps) {
  return (
    <div className="px-6 py-4">
      {activeTab === 'details' && (
        <MeetingDetailsTab
          formData={formData}
          onChange={setFormData}
          onShowRecurrence={onShowRecurrence}
        />
      )}

      {activeTab === 'templates' && (
        <TemplateList
          onSelectTemplate={(template) => {
            const startTime = formData.startTime || new Date();
            const endTime = new Date(startTime);
            endTime.setMinutes(endTime.getMinutes() + template.defaultDuration);
            
            setFormData({
              ...formData,
              type: template.type,
              title: template.name,
              description: template.description,
              startTime,
              endTime,
              agenda: template.agendaTemplate.map(item => ({
                ...item,
                id: Math.random().toString(36).substr(2, 9)
              })),
            });
            setActiveTab('details');
          }}
        />
      )}

      {activeTab === 'attendees' && (
        <AttendeeList
          attendees={formData.participants || []}
          onAddAttendee={(attendee) => {
            setFormData({
              ...formData,
              participants: [...(formData.participants || []), attendee]
            });
          }}
          onRemoveAttendee={(attendeeId) => {
            setFormData({
              ...formData,
              participants: (formData.participants || []).filter(p => p.id !== attendeeId)
            });
          }}
          onUpdateAttendance={(attendeeId, status) => {
            setFormData({
              ...formData,
              participants: (formData.participants || []).map(p =>
                p.id === attendeeId ? { ...p, attendance: status } : p
              )
            });
          }}
        />
      )}

      {activeTab === 'agenda' && (
        <AgendaBuilder
          agenda={formData.agenda ?? []}
          onChange={(agenda) => setFormData({ ...formData, agenda })}
        />
      )}

      {activeTab === 'minutes' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Meeting Minutes</label>
            <div className="mt-1 text-sm text-gray-500 mb-2">
              Created on: {new Date().toLocaleString()}
            </div>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={10}
              className="block w-full rounded-md border-2 border-gray-500 shadow-sm focus:border-gray-500 focus:ring-1 focus:ring-gray-500 resize-none"
              placeholder="Record meeting minutes, decisions, and action items..."
            />
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <TaskList
          tasks={formData.tasks || []}
          onUpdateTask={(taskId, updates) => {
            setFormData({
              ...formData,
              tasks: (formData.tasks || []).map(task =>
                task.id === taskId ? { ...task, ...updates } : task
              )
            });
          }}
        />
      )}

      {activeTab === 'files' && (
        <FileSection
          formData={formData}
          onChange={setFormData}
        />
      )}
    </div>
  );
}